/**
 * Staged report-draft sections emitted by the copilot (INSERT_REPORT_DRAFT).
 * A draft sits here until the doctor accepts it into the report editor or
 * dismisses it. Keyed by `${reportUuid}:${section}` so a newer draft for the
 * same section replaces the previous one.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/state/store';

export type DraftSection =
  | 'findings'
  | 'conclusion'
  | 'technique'
  | 'impression';

// Alias used by report-side components (CopilotDraftBanner).
export type ReportSection = DraftSection;

export interface CopilotDraft {
  id: string;
  reportUuid: string;
  section: DraftSection;
  markdown: string;
  createdAt: number;
}

interface CopilotDraftState {
  byId: Record<string, CopilotDraft>;
}

const initialState: CopilotDraftState = { byId: {} };

const draftKey = (reportUuid: string, section: DraftSection) =>
  `${reportUuid}:${section}`;

const copilotDraftSlice = createSlice({
  name: 'copilotDraft',
  initialState,
  reducers: {
    stageDraft: (
      state,
      action: PayloadAction<{
        reportUuid: string;
        section: DraftSection;
        markdown: string;
      }>
    ) => {
      const { reportUuid, section, markdown } = action.payload;
      const id = draftKey(reportUuid, section);
      state.byId[id] = {
        id,
        reportUuid,
        section,
        markdown,
        createdAt: Date.now(),
      };
    },
    clearDraft: (state, action: PayloadAction<{ id: string }>) => {
      delete state.byId[action.payload.id];
    },
  },
});

export const { stageDraft, clearDraft } = copilotDraftSlice.actions;
export default copilotDraftSlice.reducer;

export const selectDraftFor =
  (reportUuid: string, section: DraftSection) =>
  (state: RootState): CopilotDraft | undefined =>
    state.copilotDraft.byId[draftKey(reportUuid, section)];

export const selectDraftsForReport =
  (reportUuid: string) =>
  (state: RootState): CopilotDraft[] =>
    Object.values(state.copilotDraft.byId).filter(
      (d) => d.reportUuid === reportUuid
    );
